import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { usePageMeta } from "@/lib/store";
import { WRAP } from "@/components/store/Chrome";

const LABEL = { refund: "Get a refund", wait: "I'll wait for it", alternate: "Choose an alternate" };

export default function OrderChoice() {
  const [params] = useSearchParams();
  const o = params.get("o") || "";
  const t = params.get("t") || "";
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [choice, setChoice] = useState(LABEL[params.get("c")] ? params.get("c") : "");
  const [note, setNote] = useState("");
  const [done, setDone] = useState("");
  const [busy, setBusy] = useState(false);
  usePageMeta("Your order | Vinyl Record Vault");

  useEffect(() => {
    api.get("/order-choice", { params: { o, t } }).then((r) => setInfo(r.data)).catch((e) => setErr(errMsg(e, "This link is not valid")));
  }, [o, t]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/order-choice", { o, t, choice, note: choice === "alternate" ? note : "" });
      setDone(choice);
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`${WRAP} pt-14 max-w-2xl`}>
      <p className="label-caps text-[11px] text-red mb-1.5">{info ? `Order ${info.order_number}` : "Your order"}</p>
      <h1 className="font-head font-extrabold text-4xl tracking-tight border-b-2 border-black pb-4">Some items couldn't be processed</h1>
      {err && <p className="mt-8 text-red" data-testid="choice-error">{err}</p>}
      {done && (
        <div className="mt-8 flex gap-3 items-start" data-testid="choice-done">
          <CheckCircle2 className="w-6 h-6 text-[#1f7a3a] shrink-0" />
          <p>Thanks{info ? `, ${info.name.split(" ")[0]}` : ""}. We've noted your choice: <b>{LABEL[done]}</b>. Our team will be in touch shortly.</p>
        </div>
      )}
      {info && !done && (
        <div className="mt-8" data-testid="choice-form">
          {info.items.length === 0 ? <p className="text-[#555]">There's nothing waiting for your decision on this order.</p> : (
            <>
              <ul className="bg-white border border-[#e3e3e3] divide-y divide-[#eee] text-sm" data-testid="choice-items">
                {info.items.map((i) => <li key={i.product_id} className="px-4 py-3"><b className="font-arial uppercase text-xs">{i.artist}</b> – {i.title} ({i.format}) × {i.qty}{i.choice && <span className="text-[#888]"> · current choice: {LABEL[i.choice]}</span>}</li>)}
              </ul>
              <p className="mt-6 text-sm text-[#555]">What would you like us to do with these item(s)? The rest of your order continues as normal.</p>
              <div className="mt-4 grid sm:grid-cols-3 gap-3">
                {["refund", "wait", "alternate"].map((c) => (
                  <button key={c} onClick={() => setChoice(c)} data-testid={`choice-${c}`}
                    className={`h-12 border border-black text-xs font-bold tracking-[0.12em] uppercase transition-colors ${choice === c ? "bg-black text-white" : "bg-white hover:bg-[#f2f2f2]"}`}>{LABEL[c]}</button>
                ))}
              </div>
              {choice === "alternate" && (
                <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} rows={3}
                  placeholder="Which record would you like instead? (artist, title or catalogue number – optional, we can also suggest options)"
                  className="mt-3 w-full border border-[#ccc] bg-white p-3 text-sm font-arial outline-none focus:border-black" data-testid="choice-alternate-note" />
              )}
              <button disabled={!choice || busy} onClick={submit} className="mt-4 w-full h-12 bg-red text-white text-xs font-bold tracking-[0.15em] disabled:opacity-40" data-testid="choice-submit">
                {busy ? "SAVING…" : "CONFIRM MY CHOICE"}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
